import * as React from 'react';
import './chat.css';
import { useChatHost, type ChatAgentSummary } from './host.tsx';
import { renderMarkdown, stripSystemNotes } from './markdown.ts';
import { sseEvents, type Block, type ChatEvent } from './model.ts';
import { OptionsCard, type OptionsConfig } from './OptionsCard.tsx';
import {
  deleteChatReport,
  hasSqlChart,
  isReportChart,
  listChatReports,
  openReport,
  reportChartOf,
  reportUrl,
  saveChatReport,
  type ChatReportSummary,
} from './reports.ts';

// The chat itself, PORTED one to one from the old platform's public chat page (the page the old
// AssistantChat.jsx window showed in an iframe) for the orchestrator harness: the status bar (connection
// dot, project, the host's context, model, usage), the toolbar (Sessions ▾, Config ⚙, Commands ▾, the 🧩 Agents
// badge, a pinned agent), the left rail (Advanced: Saved reports; Standard: the Chats history with
// "+ New chat"), the transcript rows (its own markdown), the #thinking strip whose tool chips live only
// while a turn runs, the viz cards, and the input row (✨ Rich charts, ⎙ Attach, Send ⇄ Stop; Standard:
// the model bar and the "/" typeahead). Same ids, classes and texts as the old page (chat.css is its
// <style>). Only the transport differs: one streamed POST per turn to the assistant instead of a
// WebSocket to a headless agent process, and the api's sessions, reports and agents instead of the old
// assistant's /chat/sessions, /reports and /agents. Everything host-specific (the viz sandbox, chart
// windows, page routes, refreshes, the agents roster, dashboards) comes from the host (useChatHost).

export interface PinnedAgent {
  name: string;
  display_name?: string;
  icon?: string;
}

type Row =
  | { kind: 'user'; id: string; text: string }
  | {
      kind: 'assistant';
      id: string;
      text: string;
      /** Cards above the prose: charts, visualizations, Board tasks (chat.html .viz-card). */
      cards: Block[];
      /** A delegate's answer (chat.html appendAgentAnswer): its icon, name and guardrail verdict. */
      agent?: { icon: string; name: string; guardrail: unknown };
      complete: boolean;
      status?: string;
      /** The auto-saved report of a turn that showed charts (chat.html autoSaveReport's .save-bar). */
      save?: SaveState;
    }
  | { kind: 'status' | 'error'; id: string; text: string };

interface Chip {
  id: string;
  name: string;
  status: 'running' | 'done' | 'failed';
}
interface Progress {
  agent: string;
  tool: string;
  done: number;
  status: 'running' | 'done';
}
interface Session {
  id: string;
  title: string;
  updatedAt: string;
  preview: string | null;
}
type Report = ChatReportSummary;
type SaveState =
  | { state: 'saving' }
  | { state: 'saved'; id: string; n: number; charts: Record<string, unknown>[]; title: string }
  | { state: 'error'; message: string };
/** Which chat this is (the old harness): the orchestrator, the Data analyst, the Pipeline engine. */
export type Harness = 'orchestrator' | 'analyst' | 'engine';
type Agent = ChatAgentSummary;
interface Model {
  /** A role (fast, standard, reasoning), or one of the host LLM port's choices (another fleet key's model). */
  id: string;
  model: string;
  label: string;
}
interface Upload {
  idx: number;
  name: string;
  sizeKb: number;
  mediaType: string;
  key: string;
  size: number;
}

let seq = 0;
const makeId = () => 't-' + Math.random().toString(36).slice(2, 9) + (seq++).toString(36);

/** The chat's name where the host gives none (`ChatHost.brand`). */
export const DEFAULT_BRAND = 'Assistant';

// The connect-time hello of a fresh session (old assistant ws-handler.js one-shot hello, per harness).
const helloText = (agent: PinnedAgent | null, harness: Harness = 'orchestrator', brand = DEFAULT_BRAND) =>
  agent
    ? `Hi! You're talking to **${agent.display_name || agent.name}** — I'll route your ` +
      'requests straight to it. Just describe what you need.'
    : harness === 'engine'
      ? "Hi! I'm the pipeline engine assistant — endpoints, services and " +
        "engine-side authoring for this project's fork."
      : harness === 'analyst'
        ? "Hi! I'm the data analyst — ask questions about this project's " +
          "governed data and I'll query and chart it."
        : `Hi! I'm ${brand} — ask me anything about this project: its data, ` +
          "pipelines, workflows, agents, apps and reports. I'll answer directly " +
          'or bring in the right specialist.';
const placeholderOf = (h: Harness, brand = DEFAULT_BRAND) =>
  h === 'analyst'
    ? 'Message the data analyst…'
    : h === 'engine'
      ? 'Message the pipeline engine…'
      : `Message ${brand}…`;

// Slash commands (chat.html COMMANDS; handled here as the old agent's slashCommands.ts did).
const COMMANDS = [
  { name: '/help', desc: 'List available slash commands' },
  { name: '/clear', desc: 'Clear the visible transcript (keeps model memory)' },
  { name: '/reset', desc: 'Reset the model conversation history' },
  { name: '/exit', desc: 'End this session' },
];
const agentCommands = (brand: string): [string, string][] => [
  ['/help', 'Show the list of slash commands.'],
  ['/clear', "Clear the on-screen transcript (does not reset the model's memory of this session)."],
  ['/exit', `Exit ${brand.toLowerCase()}.`],
  ['/quit', 'Alias for /exit.'],
  ['/status', 'Show current session details.'],
  ['/reset', "Drop the model's memory of this session. Transcript stays visible; next turn starts fresh."],
  ['/sessions', 'List previously saved sessions.'],
];

const FALLBACK_MODELS: Model[] = [{ id: 'standard', model: 'standard', label: 'standard' }];
const shortModel = (m: string) => (m || '').replace(/^us\.anthropic\./, '').replace(/^anthropic\./, '');
function formatBytes(n: number) {
  if (!n) return '0';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  return (n / 1024 / 1024).toFixed(1) + ' MB';
}
function fmtTok(n: number) {
  if (!n) return '0';
  if (n < 1000) return String(n);
  if (n < 1_000_000) return (n / 1000).toFixed(1) + 'k';
  return (n / 1_000_000).toFixed(1) + 'M';
}
// chat.html renderUsage: in · out · $cost (no cached-token count: the api reports none).
const usageText = (u: { i: number; o: number; cost: number | null } | null) =>
  !u ? '—' : [`in ${fmtTok(u.i)}`, `out ${fmtTok(u.o)}`, `$${(u.cost ?? 0).toFixed(3)}`].join(' · ');

// Keyed by the harness and the APP too: an app-embedded chat's roster comes from that app's companions,
// so resuming app A's session inside app B would carry A's personas (old chat.html lastSidKey).
const lastSidKey = (harness: Harness, slug: string, app?: string) =>
  `cortex_last_sid:${harness}:${slug || '-'}${app ? ':' + app : ''}`;
// /reset: the model's memory of a session starts at this time (the transcript stays).
const memoryKey = (sid: string) => `cortex_memory_since:${sid}`;
const MAX_UPLOAD = 100 * 1024 * 1024;

/** The rows of stored messages: delegate answers become their own rows, cards land in the next row. */
function rowsFromHistory(
  messages: { id: number; role: 'user' | 'assistant'; content: string; blocks: Block[] }[],
  icons: Map<string, string>,
): Row[] {
  const out: Row[] = [];
  for (const m of messages) {
    if (m.role === 'user') {
      out.push({ kind: 'user', id: `m${m.id}`, text: m.content });
      continue;
    }
    let pending: Block[] = [];
    for (const b of m.blocks ?? []) {
      if (b.type === 'agent_answer') {
        out.push(agentRow(b, pending, icons));
        pending = [];
      } else if (isCard(b)) pending.push(b);
    }
    out.push({ kind: 'assistant', id: `m${m.id}`, text: m.content, cards: pending, complete: true });
  }
  return out;
}
const isCard = (b: Block) =>
  b.type === 'chart' || b.type === 'viz' || b.type === 'board_task' || b.type === 'propose_options';
const agentRow = (b: Block, cards: Block[], icons: Map<string, string>): Row => ({
  kind: 'assistant',
  id: makeId(),
  text: String(b.text ?? ''),
  cards,
  agent: {
    icon: icons.get(String(b.agent).replace(/^agent:/, '')) || '🤖',
    name: String(b.label ?? b.agent ?? 'agent'),
    guardrail: b.guardrail,
  },
  complete: true,
});

// ── The viz cards (chat.html addInlineViz / addCodeViz, and the Board-task card added since) ──
function VizCard({
  b,
  onFix,
  onOptions,
}: {
  b: Block;
  onFix: (m: string) => void;
  onOptions?: (text: string, title: string) => void;
}) {
  const host = useChatHost();
  const openChartWindow = host.openChartWindow;
  if (b.type === 'propose_options')
    return (
      <OptionsCard
        config={(b.config ?? {}) as OptionsConfig}
        onSubmit={(t, title) => onOptions?.(t, title)}
      />
    );
  if (b.type === 'viz' && typeof b.code === 'string') {
    const code = b.code;
    const title = String(b.title ?? '(viz)');
    return (
      <div className="viz-card" data-testid="chat-viz-card">
        <div className="viz-head">
          <div className="viz-title">{title}</div>
          <div className="viz-actions">
            {openChartWindow && (
              <button
                type="button"
                className="viz-btn"
                data-testid="chat-chart-open"
                onClick={() => openChartWindow({ title, content: { kind: 'viz', code, data: b.data } })}
              >
                Open in window
              </button>
            )}
          </div>
        </div>
        {host.renderViz ? (
          host.renderViz({
            testid: 'chat-viz',
            title,
            code,
            data: b.data,
            ...(typeof b.height === 'number' ? { height: b.height } : {}),
            onFix,
          })
        ) : (
          <pre className="viz-code">{code}</pre>
        )}
      </div>
    );
  }
  if (b.type === 'board_task') {
    const id = typeof b.id === 'number' ? b.id : null;
    return (
      <div className="viz-card" data-testid="chat-board-task">
        <div className="viz-head">
          <div className="viz-title">
            {String(b.title)}
            {id !== null ? ` · #${id}` : ''}
          </div>
        </div>
        <div className="viz-content">
          <div className="opt-intro">
            Board task · {String(b.kind ?? '').replace('_', ' ')} · {String(b.status ?? '')}
          </div>
        </div>
      </div>
    );
  }
  // A chart: the api draws its SVG from validated rows; without one, its rows as a table.
  const rows = (b.rows as Record<string, unknown>[] | undefined) ?? [];
  const cols = ((b.columns as { name: string }[] | undefined) ?? []).map((c) => c.name);
  const svg = typeof b.svg === 'string' && b.svg ? b.svg : null;
  const title = String(b.title || '(chart)') + (b.subtitle ? ' · ' + String(b.subtitle) : '');
  return (
    <div className="viz-card" data-testid="chat-chart">
      <div className="viz-head">
        <div className="viz-title">{title}</div>
        <div className="viz-actions">
          {openChartWindow && (
            <button
              type="button"
              className="viz-btn"
              data-testid="chat-chart-open"
              onClick={() =>
                openChartWindow({
                  title: String(b.title ?? 'Chart'),
                  content: { kind: 'chart', ...(svg ? { svg } : {}), columns: cols, rows },
                })
              }
            >
              Open in window
            </button>
          )}
        </div>
      </div>
      {svg ? (
        <div
          className="viz-content"
          data-testid="chat-chart-svg"
          style={{ color: 'var(--c-text)' }}
          // The SVG is rendered by the api's analytics package from validated rows (escaped text).
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      ) : (
        <div className="viz-content viz-scroll">
          {rows.length === 0 ? (
            <div style={{ padding: 6, color: 'var(--c-faint)' }}>No rows</div>
          ) : (
            <table className="viz-table">
              <thead>
                <tr>
                  {cols.map((c) => (
                    <th key={c}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    {cols.map((c) => (
                      <td key={c}>{String(r[c] ?? '')}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

/** The guardrail verdict beside a delegate's name: the platform's decision, never the agent's claim. */
function GuardrailBadge({ g }: { g: unknown }) {
  const v = g as {
    verdict?: string;
    reason?: string;
    refused?: boolean;
    checks?: { name: string; skipped?: boolean }[];
    tool_calls?: number;
  } | null;
  if (!v || !v.verdict || v.verdict === 'none') return null;
  const checks = Array.isArray(v.checks) ? v.checks.filter((c) => !c.skipped).map((c) => c.name) : [];
  const text =
    v.verdict === 'pass'
      ? '✓ checked' + (checks.length ? ': ' + checks.join(', ') : '')
      : v.refused
        ? '⛔ refused by safeguard'
        : '⚠ unverified';
  return (
    <span
      className={'agent-guardrail ' + (v.verdict === 'pass' ? 'pass' : 'fail')}
      data-verdict={v.verdict}
      data-testid="chat-guardrail"
      title={
        (v.reason ? v.reason + ' · ' : '') + (v.tool_calls != null ? v.tool_calls + ' tool call(s)' : '')
      }
    >
      {text}
    </span>
  );
}

/** The auto-save bar of a turn with charts (chat.html autoSaveReport): Saved · Open ↗ · Copy link · 📊. */
function SaveBar({ save, slug }: { save: SaveState; slug: string }) {
  const host = useChatHost();
  const [copy, setCopy] = React.useState('Copy link');
  const [dash, setDash] = React.useState<{ label: string; busy: boolean }>({
    label: '📊 Generate dashboard',
    busy: false,
  });
  if (save.state === 'saving')
    return (
      <div className="save-bar" data-testid="chat-save-bar">
        <span className="save-status">Saving…</span>
      </div>
    );
  if (save.state === 'error')
    return (
      <div className="save-bar" data-testid="chat-save-bar">
        <span className="save-status save-error">{`Auto-save failed: ${save.message}`}</span>
      </div>
    );
  const link = reportUrl(host, slug, save.id);
  return (
    <div className="save-bar" data-testid="chat-save-bar" data-report={save.id}>
      <span className="save-status saved">{`✓ Saved (${save.n} chart${save.n === 1 ? '' : 's'})`}</span>
      <button
        className="share-btn"
        data-testid="chat-report-open"
        onClick={() => openReport(host, slug, save.id)}
      >
        Open ↗
      </button>
      <button
        className="share-btn"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(link);
            setCopy('✓ Copied');
            setTimeout(() => setCopy('Copy link'), 1500);
          } catch {
            setCopy(link);
          }
        }}
      >
        {copy}
      </button>
      {hasSqlChart(save.charts) && host.generateDashboard && (
        <button
          className="share-btn"
          disabled={dash.busy}
          data-testid="chat-generate-dashboard"
          onClick={async () => {
            setDash({ label: 'Building dashboard…', busy: true });
            try {
              await host.generateDashboard!(slug, { title: save.title, charts: save.charts });
              setDash({ label: '✓ Dashboard ready', busy: true });
              setTimeout(() => setDash({ label: '📊 Generate dashboard', busy: false }), 2500);
            } catch (e) {
              setDash({ label: 'Failed: ' + ((e as Error).message || e), busy: true });
              setTimeout(() => setDash({ label: '📊 Generate dashboard', busy: false }), 3500);
            }
          }}
        >
          {dash.label}
        </button>
      )}
    </div>
  );
}

function ToolChipView({ c }: { c: Chip }) {
  return (
    <span
      className={'tool-chip ' + c.status}
      data-kind="tool"
      data-testid="chat-tool"
      data-tool={c.name}
      data-status={c.status}
    >
      <span
        className={c.status === 'running' ? 'spinner' : c.status === 'done' ? 'icon-done' : 'icon-failed'}
      />
      <span>{c.name || 'tool'}</span>
    </span>
  );
}

export function ChatBody({
  slug,
  simple: standardView,
  harness = 'orchestrator',
  app,
  seed,
  agent,
  onUnpinAgent,
  // The window's close is its own title bar's (ChatPanel); kept in the props for hosts that embed the body.
  onClose: _onClose,
  navigate: navigateProp,
  getToken: getTokenProp,
}: {
  slug: string;
  /** Standard mode (the old body.simple); it applies to the orchestrator harness only. */
  simple: boolean;
  /** Which chat: the orchestrator (default), the Data analyst, the Pipeline engine. */
  harness?: Harness;
  /** The app the chat is embedded in: the assistant gates it by that app's companions. */
  app?: string;
  /** A prompt to put into the input (never auto-sent, never over a draft). */
  seed: { text: string; n: number } | null;
  /** The project agent the chat is switched to, or null. */
  agent: PinnedAgent | null;
  onUnpinAgent: () => void;
  onClose: () => void;
  /** Default: the host's. */
  navigate?: (path: string) => void;
  /** Default: the host's. */
  getToken?: () => string | undefined;
}) {
  const host = useChatHost();
  const api = React.useMemo(() => host.clientOf(slug), [host, slug]);
  const base = host.assistantBaseUrl.replace(/\/+$/, '');
  const navigate = navigateProp ?? host.navigate;
  const getToken = getTokenProp ?? host.getToken;
  // Standard view is the orchestrator's only: engine/analyst chats keep the full chrome (old isSimple).
  const simple = standardView && harness === 'orchestrator';
  const persona = harness === 'orchestrator' ? undefined : harness;
  const [rows, setRows] = React.useState<Row[]>([]);
  const [sid, setSid] = React.useState<string | null>(null);
  const sidRef = React.useRef<string | null>(null);
  const [connected, setConnected] = React.useState<'idle' | 'connected' | 'error'>('idle');
  const [models, setModels] = React.useState<Model[]>(FALLBACK_MODELS);
  const [currentModel, setCurrentModel] = React.useState<Model['id'] | ''>('');
  const [usage, setUsage] = React.useState<{ i: number; o: number; cost: number | null } | null>(null);
  const [sessions, setSessions] = React.useState<Session[]>([]);
  const [reports, setReports] = React.useState<Report[]>([]);
  const [agents, setAgents] = React.useState<Agent[]>([]);
  const [menu, setMenu] = React.useState<'sessions' | 'config' | 'commands' | 'agents' | 'model' | null>(
    null,
  );
  const [railCollapsed, setRailCollapsed] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [chips, setChips] = React.useState<Chip[]>([]);
  const [progress, setProgress] = React.useState<Progress | null>(null);
  const [attachLabel, setAttachLabel] = React.useState<string | null>(null);
  const [richCharts, setRichCharts] = React.useState(() => {
    try {
      return localStorage.getItem('cortex_rich_charts') === '1';
    } catch {
      return false;
    }
  });
  const [cmdIdx, setCmdIdx] = React.useState(0);
  const [cmdOpen, setCmdOpen] = React.useState(false);
  const pendingUploads = React.useRef<Upload[]>([]);
  const busyRef = React.useRef(false);
  const queuedSend = React.useRef<string | null>(null);
  const sendRef = React.useRef<(t: string) => Promise<void>>(async () => undefined);
  const memorySince = React.useRef<string | null>(null);
  const uploadSeq = React.useRef(0);
  const abort = React.useRef<AbortController | null>(null);
  const transcriptRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);
  const scrollPinned = React.useRef(true);
  const agentIcons = React.useMemo(
    () => new Map(agents.map((a) => [a.key, a.icon || '🧩'] as [string, string])),
    [agents],
  );

  const authHeaders = (): Record<string, string> => {
    const t = getToken();
    return t ? { authorization: `Bearer ${t}` } : {};
  };

  const setSession = (id: string | null) => {
    sidRef.current = id;
    setSid(id);
    try {
      if (id) sessionStorage.setItem(lastSidKey(harness, slug, app), id);
      else sessionStorage.removeItem(lastSidKey(harness, slug, app));
    } catch {
      /* storage unavailable */
    }
  };

  // ── rails, menus and badges ──
  const fetchSessions = React.useCallback(async () => {
    try {
      const { data } = await api.GET('/sessions', {});
      setSessions((data?.sessions as Session[] | undefined) ?? []);
    } catch {
      setSessions([]);
    }
  }, [slug]);
  const fetchReports = React.useCallback(async () => {
    try {
      setReports(await listChatReports(api));
    } catch {
      setReports([]);
    }
  }, [slug]);
  const fetchAgents = React.useCallback(async () => {
    try {
      setAgents(host.listAgents ? await host.listAgents(slug) : []);
    } catch {
      setAgents([]);
    }
  }, [slug]);

  // The model registry (GET /assistant/v1/models): its answer is also the connection dot.
  React.useEffect(() => {
    let alive = true;
    const ctl = new AbortController();
    const t = setTimeout(() => ctl.abort(), 2500);
    fetch(`${base}/v1/models`, { headers: authHeaders(), signal: ctl.signal })
      .then(async (r) => {
        // The host's front door answers 503 `assistant_unavailable` when no assistant is reachable: the
        // chat is not connected. Any other answer means it is (an older one has no model registry: the
        // deployment's default model then, as the old chat's fallback list did).
        if (r.status === 503) {
          const b = (await r.json().catch(() => null)) as { error?: string } | null;
          if (b?.error === 'assistant_unavailable') throw new Error('assistant_unavailable');
        }
        if (alive) setConnected('connected');
        return r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`));
      })
      .then((d: { models?: Model[]; default?: Model['id'] }) => {
        if (!alive) return;
        if (Array.isArray(d.models) && d.models.length) {
          setModels(d.models);
          // The friendly names (old: the model registry's labels, "Sonnet 5 (Bedrock)"): the host's
          // label of each model id (e.g. its fleet catalog's), when this person may read it.
          void (host.modelLabels?.() ?? Promise.resolve(new Map<string, string>()))
            .then((names) => {
              if (alive && names.size)
                setModels(
                  d.models!.map((m) =>
                    names.has(m.model)
                      ? { ...m, label: names.get(m.model)! + m.label.slice(m.model.length) }
                      : m,
                  ),
                );
            })
            .catch(() => undefined);
        }
        setCurrentModel((m) => m || d.default || d.models?.[0]?.id || '');
      })
      .catch((e: Error) => alive && !/^HTTP /.test(e.message) && setConnected('error'))
      .finally(() => clearTimeout(t));
    return () => {
      alive = false;
      ctl.abort();
    };
    // Once per chat (the registry is per deployment).
  }, []);

  React.useEffect(() => {
    void fetchSessions();
    if (!simple && harness !== 'engine') void fetchReports();
    if (!simple && harness === 'orchestrator') void fetchAgents();
  }, [simple, harness, fetchSessions, fetchReports, fetchAgents]);

  // ── the transcript ──
  const clearTranscript = () => {
    setRows([]);
    setChips([]);
    setProgress(null);
  };
  const append = (row: Row) => setRows((r) => [...r, row]);
  const freshSession = React.useCallback(
    (withAgent: PinnedAgent | null = agent) => {
      abort.current?.abort();
      setSession(null);
      memorySince.current = null;
      setUsage(null);
      pendingUploads.current = [];
      uploadSeq.current = 0;
      setChips([]);
      setProgress(null);
      setRows([
        {
          kind: 'assistant',
          id: 'hello-' + makeId(),
          text: helloText(withAgent, harness, host.brand),
          cards: [],
          complete: true,
        },
      ]);
    },
    [agent, harness],
  );
  const resume = React.useCallback(
    async (id: string) => {
      abort.current?.abort();
      clearTranscript();
      setUsage(null);
      try {
        const { data } = await api.GET('/sessions/{id}', { params: { path: { id } } });
        if (!data) throw new Error('not found');
        setSession(id);
        memorySince.current = null;
        setRows(rowsFromHistory(data.messages as Parameters<typeof rowsFromHistory>[0], agentIcons));
      } catch {
        freshSession();
      }
    },
    [slug, agentIcons, freshSession],
  );

  // FIRST mount: rejoin the last session of this tab and project rather than minting an empty one.
  const firstConnect = React.useRef(true);
  React.useEffect(() => {
    if (!firstConnect.current) return;
    firstConnect.current = false;
    let recalled: string | null = null;
    try {
      recalled = sessionStorage.getItem(lastSidKey(harness, slug, app));
    } catch {
      recalled = null;
    }
    if (recalled) void resume(recalled);
    else freshSession();
  }, []);

  // Another app (or none), or another chat kind, is a different roster: a FRESH session, never the old
  // one resumed (old chat.html: prevAppView !== appView || prevHarness !== harness).
  const prevScope = React.useRef(`${harness}|${app ?? ''}`);
  React.useEffect(() => {
    const scope = `${harness}|${app ?? ''}`;
    if (scope === prevScope.current) return;
    prevScope.current = scope;
    freshSession();
  }, [harness, app]);

  // Switching (or clearing) the pinned agent is a different conversation: a fresh session.
  const prevAgent = React.useRef(agent?.name ?? null);
  React.useEffect(() => {
    const name = agent?.name ?? null;
    if (name === prevAgent.current) return;
    prevAgent.current = name;
    freshSession(agent);
  }, [agent, freshSession]);

  // A seeded prompt fills the input (never auto-sends, never clobbers a draft).
  React.useEffect(() => {
    if (!seed) return;
    setDraft((d) => (d ? d : seed.text));
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [seed]);

  // Keep the transcript pinned to the bottom unless the person scrolled up.
  React.useLayoutEffect(() => {
    const el = transcriptRef.current;
    if (el && scrollPinned.current) el.scrollTop = el.scrollHeight;
  }, [rows, chips, progress]);

  // Close the menus on any click elsewhere (chat.html: document click → closeMenusExcept(null)).
  React.useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menu]);

  // The textarea grows with its content up to 160px.
  React.useLayoutEffect(() => {
    const ia = inputRef.current;
    if (!ia) return;
    ia.style.height = 'auto';
    if (draft) ia.style.height = Math.min(ia.scrollHeight, 160) + 'px';
  }, [draft]);

  // ── a turn ──
  const runSlash = (text: string) => {
    const name = text.trim().split(/\s+/)[0]!;
    const status = (t: string) => ({ kind: 'status' as const, id: makeId(), text: t });
    const user = { kind: 'user' as const, id: makeId(), text: text.trim() };
    switch (name) {
      case '/help':
        setRows((r) => [
          ...r,
          user,
          status(
            [
              'Available commands:',
              ...agentCommands(host.brand ?? DEFAULT_BRAND).map(([u, d]) => '  ' + `${u.padEnd(16)}  ${d}`),
            ].join('\n'),
          ),
        ]);
        return true;
      case '/clear':
        setRows([
          status(
            'Transcript cleared. (Conversation history with the model is unchanged — use /reset for that.)',
          ),
        ]);
        return true;
      case '/reset':
        // The model's memory of THIS session starts now; the session and its transcript stay.
        if (sidRef.current)
          try {
            sessionStorage.setItem(memoryKey(sidRef.current), new Date().toISOString());
          } catch {
            /* storage unavailable: the memory cut lasts for this page */
          }
        memorySince.current = new Date().toISOString();
        setRows((r) => [
          ...r,
          user,
          status(
            'Conversation history with the model has been cleared. The next message starts a fresh context.',
          ),
        ]);
        return true;
      case '/status': {
        const email = host.userEmail ?? '';
        const m = models.find((x) => x.id === currentModel);
        setRows((r) => [
          ...r,
          user,
          status(
            [
              'Session:',
              `  session id: ${sidRef.current ?? '(new)'}`,
              `  user:       ${email}`,
              `  model:      ${m?.model ?? currentModel}`,
              `  workdir:    ${slug}${host.context ? `/${host.context}` : ''}`,
            ].join('\n'),
          ),
        ]);
        return true;
      }
      case '/sessions':
        setRows((r) => [
          ...r,
          user,
          status(
            sessions.length
              ? [
                  'Saved sessions:',
                  ...sessions
                    .slice(0, 20)
                    .map((s) => `  ${s.id}  ${s.updatedAt.split('T')[0]}  ${s.preview ?? s.title}`),
                ].join('\n')
              : 'No saved sessions found.',
          ),
        ]);
        return true;
      case '/exit':
      case '/quit':
        // The old agent process exited and the chat reconnected to the SAME session ~2.5 s later
        // (resumed: transcript and memory kept): the connection dot drops, then comes back.
        setRows((r) => [...r, user]);
        setConnected('idle');
        setTimeout(() => setConnected('connected'), 2500);
        return true;
      default:
        return false;
    }
  };

  const send = async (text: string) => {
    const uploads = pendingUploads.current;
    if (!text && uploads.length === 0) return;
    if (busyRef.current) return;
    // Upload markers first, one per attached file (the old agent's UPLOAD_MARKER shape).
    let body = text;
    if (uploads.length > 0) {
      const markers = uploads.map((u) => `[uploaded file ${u.idx} (${u.sizeKb} kB) — ${u.name}]`).join('\n');
      body = markers + (text ? '\n\n' + text : '');
      pendingUploads.current = [];
    }
    if (body.startsWith('/') && runSlash(body)) return;
    append({ kind: 'user', id: makeId(), text: body });
    busyRef.current = true;
    setBusy(true);
    setChips([]);
    setProgress(null);
    const ac = new AbortController();
    abort.current = ac;
    // Per-turn state: the streaming assistant row, the cards that came before it, the delegate running.
    let asstId: string | null = null;
    let pending: Block[] = [];
    let delegate: { agent: string; label: string; done: number } | null = null;
    // Every chart of the turn: a turn that showed one is saved as a report at its end.
    const turnCharts: Block[] = [];
    let finalText: string | null = null;
    const turnSid = sidRef.current;
    let since = memorySince.current;
    if (turnSid && !since)
      try {
        since = sessionStorage.getItem(memoryKey(turnSid));
      } catch {
        since = null;
      }
    const patchAsst = (
      fn: (r: Extract<Row, { kind: 'assistant' }>) => Partial<Extract<Row, { kind: 'assistant' }>>,
    ) => setRows((rs) => rs.map((r) => (r.id === asstId && r.kind === 'assistant' ? { ...r, ...fn(r) } : r)));
    const ensureAsst = (text = '') => {
      if (asstId) return;
      asstId = makeId();
      const cards = pending;
      pending = [];
      append({ kind: 'assistant', id: asstId, text, cards, complete: false });
    };
    try {
      const res = await fetch(`${base}/v1/projects/${encodeURIComponent(slug)}/chat`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          ...host.turnFields,
          message: body,
          ...(turnSid ? { sessionId: turnSid } : {}),
          ...(currentModel ? { model: currentModel } : {}),
          ...(richCharts ? { viz: 'rich' } : {}),
          ...(agent?.name ? { agent: agent.name } : persona ? { persona } : {}),
          ...(app ? { app } : {}),
          ...(turnSid && since ? { historySince: since } : {}),
          ...(uploads.length
            ? {
                attachments: uploads.map((u) => ({
                  idx: u.idx,
                  key: u.key,
                  name: u.name,
                  mediaType: u.mediaType,
                  size: u.size,
                })),
              }
            : {}),
        }),
        signal: ac.signal,
      });
      if (!res.ok || !res.body) {
        setConnected(res.status === 401 ? 'connected' : 'error');
        append({
          kind: 'error',
          id: makeId(),
          text:
            res.status === 401
              ? 'ERROR: Your session has ended; sign in again.'
              : `ERROR: The assistant is not available (${res.status}).`,
        });
        return;
      }
      setConnected('connected');
      for await (const e of sseEvents(res.body) as AsyncGenerator<ChatEvent>) {
        switch (e.type) {
          case 'session':
            if (e.sessionId !== sidRef.current) memorySince.current = null;
            setSession(e.sessionId);
            break;
          case 'run_start':
            if (e.parent) delegate = { agent: e.agent, label: e.label, done: 0 };
            break;
          case 'run_end':
            if (delegate && e.agent === delegate.agent) {
              const d = delegate;
              setProgress((p) => (p ? { ...p, done: d.done, status: 'done' } : p));
              delegate = null;
            }
            break;
          case 'tool_call':
            if (e.agent === 'orchestrator' || !delegate) {
              setChips((c) => [...c, { id: e.id, name: e.name, status: 'running' }]);
            } else {
              const d = delegate;
              setProgress({ agent: d.label, tool: e.name, done: d.done, status: 'running' });
            }
            break;
          case 'tool_result':
            setChips((c) => c.map((x) => (x.id === e.id ? { ...x, status: e.status } : x)));
            if (delegate && e.agent === delegate.agent) {
              delegate.done += 1;
              const d = delegate;
              setProgress((p) => (p ? { ...p, done: d.done } : p));
            }
            // A tool that changed the design or the Board: the page behind the chat reloads what it shows.
            if (e.status === 'done') host.onToolDone?.(e.name, slug);
            break;
          case 'block': {
            const b = e.block;
            if (b.type === 'navigate') {
              // navigate_ui: the page opens behind the chat (only a page of this project).
              const p = host.pagePath?.(b.path, slug);
              if (p) navigate(p);
            } else if (b.type === 'agent_answer') {
              const cards = pending;
              pending = [];
              append(agentRow(b, cards, agentIcons));
            } else if (isCard(b)) {
              if (isReportChart(b)) turnCharts.push(b);
              // A card attaches to this turn's assistant row once it exists, else waits for the next row.
              if (asstId) patchAsst((r) => ({ cards: [...r.cards, b] }));
              else pending.push(b);
            }
            break;
          }
          case 'text':
            if (!asstId) ensureAsst(e.delta);
            else patchAsst((r) => ({ text: r.text + e.delta }));
            break;
          case 'done':
            finalText = e.text;
            ensureAsst(e.text);
            if (pending.length) {
              const rest = pending;
              pending = [];
              patchAsst((r) => ({ cards: [...r.cards, ...rest] }));
            }
            patchAsst(() => ({ text: e.text, complete: true }));
            setRows((rs) =>
              rs.map((r) => (r.id === asstId && r.kind === 'assistant' ? { ...r, status: e.status } : r)),
            );
            setUsage((u) => ({
              i: (u?.i ?? 0) + e.usage.inputTokens,
              o: (u?.o ?? 0) + e.usage.outputTokens,
              cost: e.usage.costUsd === null ? (u?.cost ?? null) : (u?.cost ?? 0) + e.usage.costUsd,
            }));
            break;
          case 'error':
            append({ kind: 'error', id: makeId(), text: e.message || 'error' });
            break;
        }
      }
    } catch (err) {
      if (ac.signal.aborted) {
        // The old agent's cancel: whatever streamed, then "[cancelled]".
        ensureAsst('');
        patchAsst((r) => ({
          text: r.text.trim().length === 0 ? '[cancelled]' : r.text.trim() + '\n[cancelled]',
          complete: true,
        }));
      } else {
        setConnected('error');
        append({
          kind: 'error',
          id: makeId(),
          text: `The assistant is not reachable: ${(err as Error).message}`,
        });
      }
    } finally {
      if (pending.length) {
        const rest = pending;
        ensureAsst('');
        patchAsst((r) => ({ cards: [...r.cards, ...rest] }));
      }
      if (asstId) patchAsst(() => ({ complete: true }));
      // Auto-save: only a turn that showed charts earns a report (prose-only answers do not).
      if (asstId && turnCharts.length && finalText !== null && finalText.trim()) {
        const charts = turnCharts.map(reportChartOf);
        const title = text || finalText.split('\n')[0]!;
        patchAsst(() => ({ save: { state: 'saving' } }));
        void saveChatReport(api, { ...(text ? { userPrompt: text } : {}), answer: finalText, charts })
          .then((j) => {
            patchAsst(() => ({ save: { state: 'saved', id: j.id, n: charts.length, charts, title } }));
            void fetchReports();
          })
          .catch((err: Error) =>
            patchAsst(() => ({ save: { state: 'error', message: err.message || String(err) } })),
          );
      }
      abort.current = null;
      busyRef.current = false;
      setBusy(false);
      // Tool chips and delegate progress were live progress only: the turn's end wipes them.
      setChips([]);
      setProgress(null);
      void fetchSessions();
      // A feature selection sent while the turn ran goes now (old queuedGuidedSend / flushGuidedSend).
      const queued = queuedSend.current;
      queuedSend.current = null;
      if (queued) setTimeout(() => void sendRef.current(queued), 0);
    }
  };
  sendRef.current = send;

  /** The feature checklist's submit: recorded as a Requirements source, then sent (queued while a turn runs). */
  const onOptions = (selection: string, title: string) => {
    void host
      .recordFeatures?.(slug, {
        title: title.slice(0, 200),
        text: selection,
        ...(sidRef.current ? { sessionId: sidRef.current } : {}),
      })
      .catch((err: Error) =>
        append({
          kind: 'error',
          id: makeId(),
          text: `The selection was not recorded as a requirement (${err.message || err}).`,
        }),
      );
    if (busyRef.current) queuedSend.current = selection;
    else void send(selection);
  };

  const submit = () => {
    const text = draft.trim();
    if (!text && pendingUploads.current.length === 0) return;
    if (busyRef.current) return;
    setDraft('');
    setCmdOpen(false);
    void send(text);
  };
  const cancel = () => abort.current?.abort();

  const onAttach = async (files: File[]) => {
    if (!files.length) return;
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i]!;
        setAttachLabel(
          files.length === 1 ? `Uploading ${f.name}…` : `Uploading ${i + 1}/${files.length}: ${f.name}…`,
        );
        if (f.size > MAX_UPLOAD) throw new Error(`${f.name} is larger than 100 MB`);
        // Stored first in the project's object store (the old signed S3 PUT), then named in the message.
        const mediaType = f.type || 'application/octet-stream';
        const r = await api.POST('/uploads', {
          params: { query: { name: f.name, mediaType } },
          body: new Uint8Array(await f.arrayBuffer()) as never,
          headers: { 'content-type': 'application/octet-stream' },
          bodySerializer: (b: unknown) => b as BodyInit,
        });
        if (!r.response.ok || !r.data) {
          const e = r.error as { message?: string } | undefined;
          throw new Error(e?.message || `HTTP ${r.response.status}`);
        }
        const idx = ++uploadSeq.current;
        pendingUploads.current = [
          ...pendingUploads.current,
          {
            idx,
            name: f.name,
            sizeKb: Math.round(f.size / 1024),
            mediaType,
            key: r.data.key,
            size: r.data.size,
          },
        ];
        append({ kind: 'status', id: makeId(), text: `↑ uploaded ${f.name} (${formatBytes(f.size)})` });
      }
    } catch (err) {
      append({ kind: 'error', id: makeId(), text: `Upload failed: ${(err as Error).message || err}` });
    } finally {
      setAttachLabel(null);
    }
  };

  // A model / Rich-charts switch is a new session, as in the old chat.
  const pickModel = (id: Model['id']) => {
    setMenu(null);
    if (id === (currentModel || models[0]?.id)) return;
    setCurrentModel(id);
    freshSession();
    void fetchSessions();
  };
  const toggleRich = () => {
    const next = !richCharts;
    setRichCharts(next);
    try {
      localStorage.setItem('cortex_rich_charts', next ? '1' : '0');
    } catch {
      /* storage unavailable */
    }
    freshSession();
  };
  const deleteSession = async (s: Session) => {
    try {
      await api.DELETE('/sessions/{id}', { params: { path: { id: s.id } } });
      setSessions((ss) => ss.filter((x) => x.id !== s.id));
      if (s.id === sid) freshSession();
    } catch {
      /* the × stays */
    }
  };
  const deleteReport = async (r: Report) => {
    if (!confirm(`Delete report "${r.title || r.id}"?`)) return;
    try {
      await deleteChatReport(api, r.id);
      void fetchReports();
    } catch (err) {
      append({ kind: 'error', id: makeId(), text: `Delete failed: ${(err as Error).message || err}` });
    }
  };

  // In-app links in an answer route the host app in place (chat.html a.in-app-link).
  const onTranscriptClick = (e: React.MouseEvent) => {
    const a = (e.target as HTMLElement).closest?.('a.in-app-link');
    if (!a) return;
    const path = a.getAttribute('data-path');
    if (!path) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    navigate(path);
  };

  const cmdMatches =
    simple && draft.startsWith('/') && !/\s/.test(draft)
      ? COMMANDS.filter((c) => c.name.startsWith(draft.toLowerCase()))
      : [];
  const showCmd = cmdOpen && cmdMatches.length > 0;
  const pickCmd = (c: (typeof COMMANDS)[number]) => {
    setCmdOpen(false);
    setCmdIdx(0);
    setDraft('');
    void send(c.name);
  };

  const effectiveModel = models.find((m) => m.id === currentModel) ?? models[0]!;
  const toggleMenu = (m: NonNullable<typeof menu>) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenu((cur) => (cur === m ? null : m));
  };
  // The rail belongs to the orchestrator and the analyst (old: harness analyst|orchestrator).
  const railVisible = harness !== 'engine';
  const sessionTitle = (s: Session) => (s.preview || s.title || s.id || '(empty)').slice(0, 60);
  const appOf = (a: Agent) => a.covers?.find((c) => c.kind === 'app')?.key;

  return (
    <div className={'cx-chat' + (simple ? ' simple' : '')} data-testid="chat-body">
      <div
        id="status-bar"
        className={connected === 'connected' ? 'connected' : connected === 'error' ? 'error' : ''}
        data-testid="chat-status-bar"
      >
        <div className="conn-dot" />
        <div className="pill" id="pill-project">
          <strong>{slug}</strong>
        </div>
        {host.context && (
          <div className="pill" id="pill-context">
            {host.context}
          </div>
        )}
        <div className="pill" id="pill-model">
          {currentModel ? shortModel(effectiveModel.model) : '—'}
        </div>
        <div className="spacer" />
        <span
          id="agent-pinned-status"
          hidden={!agent}
          title={agent ? 'Chat switched to ' + (agent.display_name || agent.name) : undefined}
        >
          <span id="agent-pinned-status-icon">{agent?.icon || '🤖'}</span>
          <span id="agent-pinned-status-name">{agent?.display_name || agent?.name}</span>
          <button
            className="pinned-x"
            id="agent-pinned-status-x"
            title="Unpin agent"
            onClick={(e) => {
              e.stopPropagation();
              onUnpinAgent();
            }}
          >
            ×
          </button>
        </span>
        <div className="usage" id="usage-line" data-testid="chat-usage">
          {usageText(usage)}
        </div>
      </div>

      <div id="toolbar">
        <button
          className="tb-btn"
          id="btn-sessions"
          data-testid="chat-history-toggle"
          onClick={toggleMenu('sessions')}
        >
          Sessions ▾
        </button>
        <button className="tb-btn" id="btn-config" onClick={toggleMenu('config')}>
          Config ⚙
        </button>
        <button className="tb-btn" id="btn-commands" onClick={toggleMenu('commands')}>
          Commands ▾
        </button>
        <button
          className="tb-btn tb-badge"
          id="btn-agents"
          hidden={simple || harness !== 'orchestrator' || agents.length === 0}
          title={`${agents.length} project agent${agents.length === 1 ? '' : 's'} loaded`}
          onClick={toggleMenu('agents')}
        >
          <span>🧩 Agents</span>
          <span className="badge-count">{agents.length}</span>
        </button>
        <span
          className="tb-pinned"
          id="agent-pinned"
          hidden={!agent}
          title={agent ? 'Chat switched to ' + (agent.display_name || agent.name) : undefined}
        >
          <span className="pinned-icon" id="agent-pinned-icon">
            {agent?.icon || '🤖'}
          </span>
          <span className="pinned-name" id="agent-pinned-name">
            {agent?.display_name || agent?.name}
          </span>
          <button
            className="pinned-x"
            id="agent-pinned-x"
            title="Unpin agent"
            onClick={(e) => {
              e.stopPropagation();
              onUnpinAgent();
            }}
          >
            ×
          </button>
        </span>
      </div>

      <div
        className={'menu' + (menu === 'sessions' ? ' open' : '')}
        id="menu-sessions"
        data-testid="chat-sessions-menu"
      >
        <div className="menu-section">Past sessions</div>
        {sessions.length === 0 ? (
          <div className="menu-empty">No past sessions</div>
        ) : (
          sessions.slice(0, 30).map((s) => (
            <div
              key={s.id}
              className="menu-item session-item"
              data-testid={`chat-session-${s.id}`}
              onClick={() => {
                setMenu(null);
                void resume(s.id);
              }}
            >
              <div className="session-body">
                <div>{sessionTitle(s)}</div>
                <div className="sub">{s.updatedAt ? new Date(s.updatedAt).toLocaleString() : s.id}</div>
              </div>
              <button
                className="session-del"
                title="Delete session"
                onClick={(e) => {
                  e.stopPropagation();
                  (e.currentTarget as HTMLButtonElement).disabled = true;
                  void deleteSession(s);
                }}
              >
                ×
              </button>
            </div>
          ))
        )}
      </div>
      <div className={'menu' + (menu === 'config' ? ' open' : '')} id="menu-config">
        <div className="menu-section">Model</div>
        {models.map((m) => (
          <div key={m.id} className="menu-item" onClick={() => pickModel(m.id)}>
            {(m.id === currentModel ? '✓ ' : '   ') + m.label}
          </div>
        ))}
      </div>
      <div className={'menu' + (menu === 'commands' ? ' open' : '')} id="menu-commands">
        <div className="menu-section">Slash commands</div>
        {COMMANDS.map((c) => (
          <div
            key={c.name}
            className="menu-item"
            onClick={() => {
              setMenu(null);
              void send(c.name);
            }}
          >
            <div>{c.name}</div>
            <div className="sub">{c.desc}</div>
          </div>
        ))}
      </div>
      <div className={'menu' + (menu === 'agents' ? ' open' : '')} id="menu-agents">
        <div className="menu-section">Project agents</div>
        {agents.map((h) => (
          <div key={h.key} className="menu-item agent-item">
            <span className="agent-icon">{h.icon || '🧩'}</span>
            <div className="agent-body">
              <div className="agent-title">{h.title || h.key || '(unnamed)'}</div>
              {h.description && <div className="agent-desc">{h.description}</div>}
              {appOf(h) && host.appPath && (
                <button
                  className="agent-app"
                  title={appOf(h)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenu(null);
                    navigate(host.appPath!(appOf(h)!, slug));
                  }}
                >
                  Open app →
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Model picker popup for the Standard view's under-input button. */}
      <div className={'menu' + (menu === 'model' ? ' open' : '')} id="menu-model">
        <div className="menu-section">Model — switching starts a new chat</div>
        {models.map((m) => (
          <div key={m.id} className="menu-item" onClick={() => pickModel(m.id)}>
            {(m.id === effectiveModel.id ? '✓ ' : '   ') + m.label}
          </div>
        ))}
      </div>

      <div id="main">
        <aside
          id="rail"
          className={(railVisible ? 'visible' : '') + (railCollapsed ? ' collapsed' : '')}
          data-testid="chat-sessions"
        >
          <div
            id="rail-head"
            onClick={(e) => {
              if (simple && !(e.target as HTMLElement).closest('.rail-toggle')) return;
              setRailCollapsed((c) => !c);
            }}
          >
            <span className="rail-title">{simple ? 'Chats' : 'Saved reports'}</span>
            <span className="rail-toggle">{railCollapsed ? '›' : '‹'}</span>
          </div>
          <button
            id="rail-new-chat"
            type="button"
            data-testid="chat-new"
            onClick={() => {
              freshSession();
              void fetchSessions();
            }}
          >
            + New chat
          </button>
          <div id="rail-list">
            {simple ? (
              sessions.length === 0 ? (
                <div className="menu-empty">No chats yet</div>
              ) : (
                sessions.slice(0, 30).map((s) => (
                  <div
                    key={s.id}
                    className={'rail-item' + (s.id === sid ? ' active' : '')}
                    data-testid={`chat-session-${s.id}`}
                    onClick={() => {
                      if (s.id === sid) return;
                      void resume(s.id);
                    }}
                  >
                    <div className="rail-item-body">
                      <div className="rail-item-title">{sessionTitle(s)}</div>
                      <div className="rail-item-meta">
                        {s.updatedAt ? new Date(s.updatedAt).toLocaleString() : ''}
                      </div>
                    </div>
                    <div className="rail-item-actions">
                      <button
                        className="rail-item-act"
                        title="Delete chat"
                        onClick={(e) => {
                          e.stopPropagation();
                          (e.currentTarget as HTMLButtonElement).disabled = true;
                          void deleteSession(s);
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))
              )
            ) : reports.length === 0 ? (
              <div className="rail-empty">No saved reports yet</div>
            ) : (
              reports.map((rep) => (
                <div
                  key={rep.id}
                  className="rail-item"
                  data-id={rep.id}
                  data-testid={`chat-report-${rep.id}`}
                >
                  <div
                    className="rail-item-body"
                    title={'Open ' + (rep.title || rep.id)}
                    onClick={() => openReport(host, slug, rep.id)}
                  >
                    <div className="rail-item-title">{rep.title || '(untitled)'}</div>
                    <div className="rail-item-meta">
                      <span>{rep.createdAt ? new Date(rep.createdAt).toLocaleDateString() : ''}</span>
                      {rep.chartCount > 0 && (
                        <span>{`${rep.chartCount} chart${rep.chartCount === 1 ? '' : 's'}`}</span>
                      )}
                    </div>
                  </div>
                  <div className="rail-item-actions">
                    <button
                      className="rail-item-act del"
                      title="Delete report"
                      onClick={(e) => {
                        e.stopPropagation();
                        void deleteReport(rep);
                      }}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          {/* SAVED GUIDES: hidden in the old chat too (guides have their own page since 2026-08-21). */}
          <div id="rail-guides-head" hidden>
            Saved guides
          </div>
          <div id="rail-guides-list" hidden />
        </aside>

        <div id="chat-col">
          <button id="rail-open-btn" type="button" title="Show chats" onClick={() => setRailCollapsed(false)}>
            ☰
          </button>
          <div
            id="transcript"
            ref={transcriptRef}
            data-testid="chat-transcript"
            onClick={onTranscriptClick}
            onScroll={(e) => {
              const el = e.currentTarget;
              scrollPinned.current = el.scrollTop + el.clientHeight >= el.scrollHeight - 16;
            }}
          >
            {rows.map((r) => {
              if (r.kind === 'user')
                return (
                  <div
                    key={r.id}
                    className="row user"
                    data-kind="user"
                    data-testid="chat-message-user"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(stripSystemNotes(r.text)) }}
                  />
                );
              if (r.kind !== 'assistant')
                return (
                  <div
                    key={r.id}
                    className={'row ' + r.kind}
                    data-kind={r.kind}
                    data-testid={r.kind === 'error' ? 'chat-error' : 'chat-status'}
                  >
                    {r.text}
                  </div>
                );
              const empty = !r.text.trim() && r.cards.length === 0 && !r.agent;
              if (r.agent)
                return (
                  <div
                    key={r.id}
                    className="row assistant agent-answer"
                    data-kind="assistant"
                    data-testid="chat-agent-answer"
                    data-agent={r.agent.name}
                  >
                    {r.cards.map((b, i) => (
                      <VizCard key={i} b={b} onFix={(m) => void send(m)} onOptions={onOptions} />
                    ))}
                    <div className="agent-answer-head">
                      <span className="agent-answer-icon">{r.agent.icon}</span>
                      <span className="agent-answer-name">{r.agent.name}</span>
                      <GuardrailBadge g={r.agent.guardrail} />
                    </div>
                    <div
                      className="agent-answer-body"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(r.text) }}
                    />
                  </div>
                );
              return (
                <div
                  key={r.id}
                  className={'row assistant' + (empty ? ' empty' : '')}
                  data-kind="assistant"
                  data-testid="chat-message-assistant"
                  data-complete={r.complete}
                  data-status={r.status ?? ''}
                >
                  {r.cards.map((b, i) => (
                    <VizCard key={i} b={b} onFix={(m) => void send(m)} onOptions={onOptions} />
                  ))}
                  <div
                    className="msg-body"
                    data-testid="chat-answer-text"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(r.text) }}
                  />
                  {r.save && <SaveBar save={r.save} slug={slug} />}
                </div>
              );
            })}
          </div>

          {/* Thinking indicator: only while a turn is in flight; its tool chips vanish at turn end. */}
          <div id="thinking" hidden={!busy} data-testid="chat-thinking">
            <span className="thinking-label">
              <span className="spinner" />
              <span className="thinking-text">Thinking…</span>
            </span>
            <span id="thinking-tools">
              {chips.map((c) => (
                <ToolChipView key={c.id} c={c} />
              ))}
              {progress && (
                <span
                  className="tool-chip running"
                  id="agent-progress-chip"
                  data-testid="chat-agent-progress"
                >
                  <span className={progress.status === 'done' ? 'icon-done' : 'spinner'} />
                  <span>
                    {[progress.agent, progress.tool].filter(Boolean).join(' · ') + ` (${progress.done})`}
                  </span>
                </span>
              )}
            </span>
          </div>

          <div id="input-row">
            {/* Slash-command typeahead (Standard view): typing "/" pops this. */}
            <div id="cmd-suggest" className={showCmd ? 'open' : ''}>
              {showCmd &&
                cmdMatches.map((c, i) => (
                  <div
                    key={c.name}
                    className={'cmd-item' + (i === cmdIdx ? ' sel' : '')}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickCmd(c);
                    }}
                  >
                    <div>{c.name}</div>
                    <div className="sub">{c.desc}</div>
                  </div>
                ))}
            </div>
            <textarea
              id="input-area"
              ref={inputRef}
              data-testid="chat-input"
              placeholder={placeholderOf(harness, host.brand)}
              rows={1}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setCmdOpen(true);
                if (cmdIdx >= cmdMatches.length) setCmdIdx(0);
              }}
              onBlur={() => setTimeout(() => setCmdOpen(false), 150)}
              onKeyDown={(e) => {
                if (showCmd) {
                  const items = cmdMatches;
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setCmdIdx((cmdIdx + 1) % items.length);
                    return;
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setCmdIdx((cmdIdx - 1 + items.length) % items.length);
                    return;
                  }
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    pickCmd(items[Math.max(cmdIdx, 0)]!);
                    return;
                  }
                  if (e.key === 'Escape') {
                    setCmdOpen(false);
                    return;
                  }
                  if (e.key === 'Tab') {
                    e.preventDefault();
                    setDraft(items[Math.max(cmdIdx, 0)]!.name);
                    return;
                  }
                }
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
                if (e.key === 'Escape' && busy) cancel();
              }}
            />
            <div id="input-controls">
              <button
                id="rich-charts-btn"
                type="button"
                className={richCharts ? 'on' : ''}
                data-testid="chat-rich-charts"
                title="Rich charts — the assistant builds custom interactive, animated visualizations instead of standard charts. Switching starts a new chat."
                onClick={toggleRich}
              >
                ✨ Rich charts
              </button>
              <button
                id="attach-btn"
                data-testid="chat-attach"
                disabled={attachLabel !== null}
                onClick={() => fileRef.current?.click()}
              >
                {attachLabel ?? '⎙ Attach'}
              </button>
              <button
                id="send-btn"
                data-testid="chat-send"
                data-state={busy ? 'stop' : 'send'}
                className={busy ? 'stop' : ''}
                onClick={() => (busy ? cancel() : submit())}
              >
                {busy ? 'Stop' : 'Send'}
              </button>
            </div>
            {/* Standard view: current model + picker (left), session usage (right). */}
            <div id="model-bar">
              <button
                id="model-btn"
                type="button"
                title="Switch model — starts a new chat"
                onClick={toggleMenu('model')}
              >
                {effectiveModel.label + ' ▾'}
              </button>
              <span id="model-usage">{usageText(usage)}</span>
            </div>
          </div>
        </div>
      </div>

      <input
        type="file"
        id="file-input"
        ref={fileRef}
        style={{ display: 'none' }}
        multiple
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          void onAttach(files);
        }}
      />
    </div>
  );
}
